/**
 * CAOS-362 — the workspace's ONE predicate vocabulary.
 *
 * A handful of authored things ask "does this apply here?" against a single record or roster row: a
 * roster row action's `appliesWhen`, a record action's `appliesWhen`. They all mean the same thing,
 * so they read the same sentence: one or more `field == "value"` / `field != "value"` clauses joined
 * by `&&`, ALL of which must hold.
 *
 * It is deliberately tiny and PURE — it names fields and literals, never a function, never a fetch.
 * A predicate is authored metadata, and metadata that could call something would be code.
 *
 * An unparseable clause fails OPEN (it matches). A predicate nobody can read must not silently hide
 * a valid action: a button that quietly never appears is indistinguishable from one that was never
 * authored, and that is the hardest kind of absence to notice.
 */

/** Read a dotted JSON path (`plan.name`, `counts.production`) out of a payload; `undefined` if absent. */
export function readJsonPath(payload: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>(
    (acc, seg) => (acc != null && typeof acc === 'object' ? (acc as Record<string, unknown>)[seg] : undefined),
    payload,
  );
}

/**
 * The one clause shape, named once.
 *
 * Exported through {@link predicateParses} rather than directly, because a second reader of this
 * pattern should be asking "is this a predicate I can read?" and not re-deriving the answer from
 * the groups. CAOS-150 needs that question: an `appliesWhen` fails OPEN when it cannot be read (see
 * the header), and a guidance topic's `visibility` must fail CLOSED, so the two share this
 * vocabulary while differing on what an unreadable sentence means.
 */
const CLAUSE = /^\s*([A-Za-z_][\w.]*)\s*(==|!=)\s*(.*?)\s*$/;

/**
 * CAOS-150 — whether every clause of a predicate is one this vocabulary can actually read.
 *
 * Needed only by callers for which an unreadable predicate must not fail open. {@link rowMatches}
 * does not expose it because its own answer already is "open", deliberately.
 */
export function predicateParses(expr: string): boolean {
  return expr.split('&&').every((clause) => CLAUSE.test(clause));
}

/** Evaluate ONE `field == "value"` / `field != "value"` clause against a row. Unparseable ⇒ true (open). */
function clauseMatches(expr: string, row: Record<string, unknown>): boolean {
  const m = CLAUSE.exec(expr);
  if (!m) return true;
  const [, field, op, rawLit] = m;
  const literal = rawLit!.trim().replace(/^["']|["']$/g, '');
  const actual = String(readJsonPath(row, field!) ?? '');
  return op === '==' ? actual === literal : actual !== literal;
}

/**
 * Evaluate an `appliesWhen` predicate — `field == "value"` clauses joined by `&&`
 * (e.g. `approvalStatus == "approved" && convertedTicket == ""`), all of which must hold.
 */
export function rowMatches(expr: string, row: Record<string, unknown>): boolean {
  return expr.split('&&').every((clause) => clauseMatches(clause, row));
}

/**
 * Fill `{field}` placeholders in a path from a record — `/sales/accounts/{account}` against a contact
 * becomes `/sales/accounts/<the account id>`. A placeholder naming something the record does not
 * carry leaves the path INCOMPLETE, and the caller is expected to refuse to offer it: half a path
 * lands somewhere arbitrary, which is worse than not offering the button.
 */
export function fillPath(template: string, row: Record<string, unknown>): { path: string; complete: boolean } {
  let complete = true;
  const path = template.replace(/\{([A-Za-z_][\w.]*)\}/g, (_all, name: string) => {
    const value = readJsonPath(row, name);
    if (value == null || value === '') {
      complete = false;
      return '';
    }
    return encodeURIComponent(String(value));
  });
  return { path, complete };
}
